import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { klaviyoViewedProduct } from "@/lib/klaviyo";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { BuyButton } from "@/components/site/BuyButton";
import { GuaranteeLine, GuaranteeBadge } from "@/components/site/Guarantee";
import { FaqList } from "@/components/site/FaqList";
import { Testimonials } from "@/components/site/Testimonials";
import { useShop } from "@/lib/shop";
import { formatMoney } from "@/lib/geo";
import { track } from "@/lib/track";
import { shopifyProductView } from "@/lib/shopifyAnalytics";
import { FAQS, EXCERPT } from "@/lib/content";
import { ChapterList, WorksheetList } from "@/components/site/Curriculum";
import { Stats } from "@/components/site/Stats";
import { PaymentBadges, DeliveryInfo } from "@/components/site/TrustBadges";
import { MobileBuyBar } from "@/components/home/FinalCta";
import { X } from "lucide-react";

const FOR = ["You have no business yet and do not know where to start", "You have a job or a family and need a clear order of steps", "You are tired of arriving late to crowded trends", "You want a system you can repeat, not a one-off tip"];
const NOT_FOR = ["You are looking for a get-rich-quick shortcut", "You want someone to build the business for you", "You will not spend a few focused hours a week on it"];

const ForWho = () => (
  <section className="border-t border-line" data-testid="for-who">
    <div className="mx-auto grid max-w-7xl gap-px bg-line px-0 md:grid-cols-2">
      <Reveal className="bg-[#050505] px-6 py-16 md:px-12 md:py-24">
        <p className="eyebrow">This is for you if</p>
        <ul className="mt-8 space-y-5">{FOR.map((t) => <li key={t} className="flex gap-4 text-base text-zinc-200 md:text-lg"><Check size={20} className="mt-1 shrink-0 text-cta" />{t}</li>)}</ul>
      </Reveal>
      <Reveal delay={0.1} className="bg-[#050505] px-6 py-16 md:px-12 md:py-24">
        <p className="eyebrow">This is not for you if</p>
        <ul className="mt-8 space-y-5">{NOT_FOR.map((t) => <li key={t} className="flex gap-4 text-base text-zinc-400 md:text-lg"><X size={20} className="mt-1 shrink-0 text-zinc-500" />{t}</li>)}</ul>
      </Reveal>
    </div>
  </section>
);
import { useSeo } from "@/lib/useSeo";
import { productSchema } from "@/lib/schema";

const PlanPicker = ({ p, installment, setInstallment }) => (
  <div className="mt-8 grid gap-3" role="radiogroup" data-testid="bundle-plan-picker">
    {[
      { v: false, label: "Pay in full", price: formatMoney(p.price, p.currency), id: "plan-full" },
      { v: true, label: "2 instalments", price: `2 × ${formatMoney(p.installment, p.currency)}`, id: "plan-installment" },
    ].map((o) => (
      <button key={o.id} type="button" role="radio" aria-checked={installment === o.v} data-testid={o.id} onClick={() => setInstallment(o.v)}
        className={`flex items-center justify-between border px-5 py-4 text-left transition-colors ${installment === o.v ? "border-cta bg-[#0a0f1f]" : "border-line hover:border-zinc-500"}`}>
        <span className="flex items-center gap-3">
          <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${installment === o.v ? "border-cta" : "border-zinc-500"}`}>
            {installment === o.v && <span className="h-2 w-2 rounded-full bg-cta" />}
          </span>
          <span className="text-white">{o.label}</span>
        </span>
        <span className="font-display text-white">{o.price}</span>
      </button>
    ))}
  </div>
);

const Gallery = ({ p }) => {
  const imgs = Array.from(new Set(p.gallery?.length ? p.gallery : [p.image]));
  const [active, setActive] = useState(0);
  return (
    <div>
      <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }}
        transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
        className="relative aspect-[3/2] overflow-hidden border border-line bg-[#0b0b0b]">
        {imgs.map((src, i) => (
          <img key={src} src={src} alt={`${p.name} ${i + 1}`} fetchpriority={i === 0 ? "high" : "low"} loading={i === 0 ? "eager" : "lazy"}
            data-testid={i === active ? "product-image" : undefined}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${i === active ? "opacity-100" : "opacity-0"}`} />
        ))}
      </motion.div>
      {imgs.length > 1 && (
        <div className="mt-3 grid grid-cols-6 gap-2 sm:grid-cols-7" data-testid="product-gallery">
          {imgs.slice(0, 7).map((src, i) => (
            <button key={src} type="button" onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} aria-label={`Image ${i + 1}`}
              className={`aspect-[3/2] overflow-hidden border transition-colors ${i === active ? "border-cta" : "border-line hover:border-zinc-500"}`}>
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductHero = ({ p }) => {
  const [installment, setInstallment] = useState(false);
  const { products } = useShop();
  const isBundle = p.role === "bundle";
  const fmt = (n) => formatMoney(n, p.currency);
  return (
    <section className="relative pt-28 md:pt-36">
      <div className="spotlight pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 pb-20 md:px-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7"><div className="lg:sticky lg:top-24"><Gallery p={p} /></div></div>
        <div className="lg:col-span-5 lg:pt-6">
          <p className="eyebrow" data-testid="product-kind">Digital {p.kind} &middot; Instant download</p>
          <h1 className="font-display mt-5 text-4xl font-medium leading-[1.04] tracking-tight text-white sm:text-5xl" data-testid="product-title">
            <MaskedLines lines={[p.name]} />
          </h1>
          <p className="mt-6 text-base leading-relaxed text-zinc-300 md:text-lg">{p.tagline}</p>
          <p className="font-display mt-8 text-4xl font-semibold text-white" data-testid="product-price">
            {installment ? `2 × ${fmt(p.installment)}` : fmt(p.price)}
          </p>
          <p className="mt-2 text-xs text-zinc-400" data-testid="product-price-note">
            {installment ? `${fmt(p.installment)} today, ${fmt(p.installment)} automatically in 1 month · Total ${fmt(p.price)} · Ends after payment 2` : "One-time payment · Digital product, nothing is shipped"}
          </p>
          {isBundle && <PlanPicker p={p} installment={installment} setInstallment={setInstallment} />}
          <BuyButton role={p.role} installment={installment} testId="product-buy-button" className="mt-8 w-full text-base">
            {isBundle ? (installment ? "Start with instalment 1" : "Get the Bundle") : `Get the ${p.kind}`}
          </BuyButton>
          <div className="mt-5"><GuaranteeLine /></div>
          <PaymentBadges className="mt-6" />
          <div className="mt-8"><DeliveryInfo role={p.role} /></div>
          {p.role === "workbook" && (
            <p className="mt-8 border-l-2 border-cta pl-4 text-sm text-zinc-300">Designed to be used with the eBook. Starting fresh? The <Link to="/products/bundle" className="link-u text-white">Bundle</Link> gives you both.</p>
          )}
          {p.role === "ebook" && (
            <p className="mt-8 border-l-2 border-cta pl-4 text-sm text-zinc-300">Want to apply it straight away? The <Link to="/products/bundle" className="link-u text-white">Bundle</Link> adds the Workbook for {fmt(Math.max(0, products.bundle.price - p.price))} more.</p>
          )}
        </div>
      </div>
    </section>
  );
};

const Includes = ({ p }) => (
  <section className="border-t border-line">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow">Inside</p>
        <h2 className="font-display mt-5 text-4xl font-medium tracking-tight text-white md:text-5xl">What is included.</h2>
      </Reveal>
      <ul className="lg:col-span-8" data-testid="product-includes">
        {p.includes.map((it, i) => (
          <Reveal key={it} delay={i * 0.05} y={14}>
            <li className="flex gap-5 border-b border-line py-6 text-base text-zinc-200 md:text-lg">
              <Check size={20} className="mt-1 shrink-0 text-white" />{it}
            </li>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Excerpt = () => (
  <section className="border-t border-line bg-[#080808]" data-testid="ebook-excerpt">
    <div className="mx-auto max-w-5xl px-6 py-24 md:px-12 md:py-32">
      <Reveal>
        <p className="eyebrow">From the book · {EXCERPT.chapter}</p>
        <blockquote className="font-serif-i mt-8 text-3xl leading-snug text-zinc-100 md:text-5xl">“{EXCERPT.text}”</blockquote>
        <p className="mt-8 text-sm text-zinc-400">Dilara Yilmaz, First Mover Blueprint</p>
      </Reveal>
    </div>
  </section>
);

const TOGETHER = [
  { t: "Read the lesson", d: "Each eBook lesson explains one part of the framework with a story and a clear takeaway." },
  { t: "Fill the worksheet", d: "The matching worksheet turns it into your goals, scores and dates, with prompts and examples." },
  { t: "Make the move", d: "You finish with a concrete next action, not just new knowledge. Then review and refine." },
];

const Together = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="bundle-together">
    <Reveal><p className="eyebrow">How the two work together</p>
      <h2 className="font-display mt-5 max-w-3xl text-4xl font-medium tracking-tight text-white md:text-5xl">Learn it in the evening. <span className="font-serif-i text-zinc-400">Apply it the same night.</span></h2></Reveal>
    <div className="mt-14 grid gap-px bg-line md:grid-cols-3">
      {TOGETHER.map((s, i) => (
        <Reveal key={s.t} delay={i * 0.1} className="h-full">
          <div className="h-full bg-[#050505] p-8 md:p-10">
            <motion.span initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 + i * 0.25 }}
              className="block h-px w-16 origin-left bg-cta" />
            <p className="font-display mt-6 text-sm text-zinc-500">0{i + 1}</p>
            <h3 className="font-display mt-2 text-2xl text-white">{s.t}</h3>
            <p className="mt-3 text-zinc-400">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Curriculum = ({ role }) => (
  <section className="border-t border-line" data-testid="product-curriculum">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
      {role !== "workbook" && (
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4"><p className="eyebrow">The eBook</p>
            <h2 className="font-display mt-5 text-4xl font-medium tracking-tight text-white">6 chapters, 25 lessons.</h2>
            <p className="mt-4 text-zinc-400">Tap a chapter to see every lesson.</p></Reveal>
          <div className="lg:col-span-8"><ChapterList /></div>
        </div>
      )}
      {role !== "ebook" && (
        <div className={`grid gap-12 lg:grid-cols-12 ${role === "bundle" ? "mt-28" : ""}`}>
          <Reveal className="lg:col-span-4"><p className="eyebrow">The Workbook</p>
            <h2 className="font-display mt-5 text-4xl font-medium tracking-tight text-white">11 worksheets, 180 pages.</h2>
            <p className="mt-4 text-zinc-400">Each one with a step-by-step guide, prompts and worked examples.</p></Reveal>
          <div className="lg:col-span-8"><WorksheetList /></div>
        </div>
      )}
    </div>
  </section>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { products } = useShop();
  const p = products[slug];
  useSeo(p?.name, p?.tagline, p ? productSchema(p) : null);
  useEffect(() => {
    if (p) track("view_item", { content_name: p.name, value: p.price, currency: p.currency });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (p?.shopifyId) { shopifyProductView(p); klaviyoViewedProduct(p); }
  }, [slug, p?.shopifyId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <Navigate to="/products" replace />;
  return (
    <main data-testid={`product-page-${slug}`}>
      <ProductHero p={p} />
      <Includes p={p} />
      <ForWho />
      {slug === "bundle" && <Together />}
      <Curriculum role={slug} />
      {slug !== "workbook" && <Excerpt />}
      <div className="mx-auto max-w-7xl px-6 pt-24 md:px-12"><Stats /></div>
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="flex flex-col items-start gap-10 border border-line p-8 md:flex-row md:items-center md:p-12">
          <GuaranteeBadge size={170} />
          <div>
            <h2 className="font-display text-3xl font-medium tracking-tight text-white md:text-4xl">7 days to decide. Risk-free.</h2>
            <p className="mt-4 max-w-xl text-zinc-300">If it is not for you, send one email within 7 days and you get a full refund.</p>
          </div>
        </div>
      </section>
      <Testimonials />
      <section className="border-t border-line bg-[#080808]">
        <div className="mx-auto max-w-4xl px-6 py-24 md:px-12"><FaqList items={FAQS.slice(0, 5)} /></div>
      </section>
      <MobileBuyBar role={slug} />
    </main>
  );
}
